import type { CollaboratorPrivate, Resource } from '@/features/Collaborators/Models';
import {
    ACADEMY_PROJECT_KEYWORDS,
    DEVELOPER_RESOURCE_TYPE_PREFIX,
    INACTIVE_COLLABORATOR_STATUSES,
    INTERNAL_PROJECT_KEYWORDS,
} from '../exportsPage.definitions';
import type {
    AllocationScope,
    CapabilityNameMaps,
    DeveloperCapacityItem,
} from '../exportsPage.types';
import { parseDateOnly } from './parseDateOnly';

/**
 * Normaliza texto para comparação: sem acento, minúsculo e com pontuação
 * virando espaço — assim "Raro-Digital" e "raro digital" casam entre si.
 */
export function normalizeText(value: string | null | undefined): string {
    if (!value) {
        return '';
    }

    return value
        .normalize('NFD')
        .replace(/[̀-ͯ]/g, '')
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, ' ')
        .trim();
}

/** Tipo de recurso de desenvolvedor: "Desenvolvedor Backend", "Desenvolvedor Mobile"... */
export function isDeveloperResourceTypeName(name: string | null | undefined): boolean {
    return normalizeText(name).startsWith(DEVELOPER_RESOURCE_TYPE_PREFIX);
}

/** Recurso marcado como principal, ou o primeiro cadastrado. */
function findPrimaryResource(resources: Resource[] | undefined): Resource | null {
    if (!resources || resources.length === 0) {
        return null;
    }
    return resources.find((resource) => resource.isPrimary) ?? resources[0];
}

/**
 * Classifica a alocação pelo nome da iniciativa: Academy, projeto interno
 * (OnVue, Raro Digital) ou as demais.
 */
export function classifyAllocationScope(projectName: string | null | undefined): AllocationScope {
    const normalized = normalizeText(projectName);
    if (!normalized) {
        return 'other';
    }

    if (ACADEMY_PROJECT_KEYWORDS.some((keyword) => normalized.includes(keyword))) {
        return 'academy';
    }

    if (INTERNAL_PROJECT_KEYWORDS.some((keyword) => normalized.includes(keyword))) {
        return 'internal';
    }

    return 'other';
}

/**
 * Seleciona os colaboradores desenvolvedores — os que têm a habilidade
 * principal de um tipo de recurso "Desenvolvedor *" — já reduzidos à janela de
 * vínculo usada para contar capacidade.
 */
export function selectDevelopers(
    collaborators: CollaboratorPrivate[],
    catalog: CapabilityNameMaps
): DeveloperCapacityItem[] {
    return collaborators
        .filter((collaborator) => {
            const primary = findPrimaryResource(collaborator.resources);
            if (!primary) {
                return false;
            }
            return isDeveloperResourceTypeName(catalog.resourceTypeMap.get(primary.typeId));
        })
        .map((collaborator) => ({
            id: collaborator.id,
            name: collaborator.name?.trim() ?? '',
            admissionDate: parseDateOnly(collaborator.startDate),
            terminationDate: parseDateOnly(collaborator.endDate),
            isInactive: isInactiveStatus(collaborator.status),
        }));
}

function isInactiveStatus(status: string | null | undefined): boolean {
    const normalized = status?.trim().toLowerCase();
    return Boolean(normalized) && INACTIVE_COLLABORATOR_STATUSES.includes(normalized as never);
}
